import type {
  DataAssetTreeDirectoryResponse,
  DataAssetWithManifestAndRealAssetDeploymentResponse,
  DirectoryInventoryListItem,
  DirectoryResponse,
  RealAssetResponse,
  User,
} from "@/apiClient";
import { extractFileName } from "@/components/dataBrowse/utils";
import { titleCase } from "@/utils";
import { z } from "zod";

export const TOKEN_SEGMENT_PATTERN = /^\{\{([^{}]+)\}\}$/;
const parseBooleanSearchParam = (value: unknown) =>
  value === true || value === "true";

export const folderConfigSearchSchema = z.object({
  tab: z.enum(["needs-docs", "documented"]).default("needs-docs"),
  rightTab: z.enum(["settings", "preview"]).default("settings"),
  suggestedQuery: z.string().default(""),
  documentedQuery: z.string().default(""),
  templatePath: z.string().default(""),
  editingDirectoryPublicId: z.string().default(""),
  previewMatchKey: z.string().default(""),
  manual: z.preprocess(parseBooleanSearchParam, z.boolean().default(false)),
});

export type FolderConfigSearch = z.infer<typeof folderConfigSearchSchema>;

export const folderConfigSearchDefaults = folderConfigSearchSchema.parse({});

export const stripFolderConfigSearchDefaults = (search: FolderConfigSearch) =>
  Object.fromEntries(
    Object.entries(search).filter(
      ([key, value]) =>
        JSON.stringify(value) !==
        JSON.stringify(
          folderConfigSearchDefaults[key as keyof FolderConfigSearch]
        )
    )
  );

export type PathSegment = {
  id: string;
  kind: "literal" | "token";
  value: string;
};

export type TokenPickerTarget = {
  mode: "insert" | "replace";
  index: number;
};

export type SegmentComposerState =
  | null
  | { mode: "chooser" }
  | { mode: "literal"; draft: string }
  | { mode: "real-asset"; target: TokenPickerTarget };

export type PreviewDirectoryListItem = DirectoryInventoryListItem;

export type DocumentedDirectoryRow = {
  definition: DirectoryResponse;
  inventory?: DirectoryInventoryListItem;
  curatedBy?: User;
  dataExpert?: User;
};

export type ExampleDirectoryItem = {
  name: string;
  path: string;
  type: "folder" | "file";
};

export const createSegment = (
  kind: PathSegment["kind"],
  value: string
): PathSegment => ({
  id: `${kind}-${value}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`,
  kind,
  value,
});

export const normalizeDirectoryPath = (path: string) =>
  path
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join("/");

export const buildTemplatePath = (segments: PathSegment[]) =>
  segments
    .map((segment) =>
      segment.kind === "token" ? `{{${segment.value}}}` : segment.value.trim()
    )
    .filter(Boolean)
    .join("/");

export const getDisplaySegmentsForTemplatePath = (templatePath: string) => {
  const normalized = normalizeDirectoryPath(templatePath);
  if (!normalized) {
    return [] as PathSegment[];
  }

  return normalized.split("/").map((segment) => {
    const tokenMatch = segment.match(TOKEN_SEGMENT_PATTERN);
    if (!tokenMatch) {
      return createSegment("literal", segment);
    }

    return createSegment("token", tokenMatch[1].trim());
  });
};

export const parseTemplatePath = (
  templatePath: string,
  realAssetsById: Map<string, RealAssetResponse>
) => {
  const normalized = normalizeDirectoryPath(templatePath);
  if (!normalized) {
    return {
      error: null as string | null,
      segments: [] as PathSegment[],
    };
  }

  const segments = normalized.split("/");
  const nextSegments: PathSegment[] = [];
  for (const segment of segments) {
    const tokenMatch = segment.match(TOKEN_SEGMENT_PATTERN);
    if (!tokenMatch) {
      if (segment.includes("{{") || segment.includes("}}")) {
        return {
          error: "Deployment ID placeholders must occupy a full folder name.",
          segments: [] as PathSegment[],
        };
      }
      nextSegments.push(createSegment("literal", segment));
      continue;
    }

    const tokenPublicId = tokenMatch[1].trim();
    if (!realAssetsById.has(tokenPublicId)) {
      return {
        error:
          "This pattern references a Real Asset that is no longer available.",
        segments: [] as PathSegment[],
      };
    }
    nextSegments.push(createSegment("token", tokenPublicId));
  }

  return {
    error: null as string | null,
    segments: nextSegments,
  };
};

export const getLiteralSegmentValidationError = (segmentValue: string) => {
  const trimmedSegment = segmentValue.trim();

  if (!trimmedSegment) {
    return "Folder names can't be blank.";
  }

  if (trimmedSegment.includes("/")) {
    return "Just one folder at a time — no slashes needed.";
  }

  if (trimmedSegment.includes("{{") || trimmedSegment.includes("}}")) {
    return "For variable folder names, use a real asset segment instead.";
  }

  return null;
};

export const compareGroupedInventory = (
  a: DirectoryInventoryListItem,
  b: DirectoryInventoryListItem
) =>
  (b.concrete_directory_count ?? 0) - (a.concrete_directory_count ?? 0) ||
  (b.cruise_count ?? 0) - (a.cruise_count ?? 0) ||
  (a.canonical_path || "").localeCompare(b.canonical_path || "");

export const manifestLabel = (manifestType: string) =>
  titleCase(manifestType)
    .replace(/\bRov\b/g, "ROV")
    .replace(/\bUsbl\b/g, "USBL")
    .replace(/\bHipap\b/g, "HiPAP")
    .replace(/\bCtd\b/g, "CTD");

export const realAssetTypeLabel = (assetType?: string | null) =>
  assetType ? manifestLabel(assetType) : "Real Asset";

export const realAssetTypeBadgeClasses = (assetType?: string | null) => {
  switch (assetType) {
    case "camera":
      return "bg-sky-100 text-sky-700";
    case "rov":
      return "bg-[#fff4da] text-[#8a5400]";
    case "positioning_system":
      return "bg-violet-100 text-violet-700";
    case "vessel":
    case "tender_vessel":
      return "bg-teal-100 text-teal-700";
    case "submersible":
      return "bg-cyan-100 text-cyan-700";
    case "multibeam_echosounder":
    case "split_beam_echosounder":
      return "bg-emerald-100 text-emerald-700";
    case "sub_bottom_profiler":
    case "acoustic_doppler_current_profiler":
    case "sound_speed_equipment":
      return "bg-fuchsia-100 text-fuchsia-700";
    default:
      return "bg-slate-100 text-blue-300";
  }
};

export const renderSegmentLabel = (
  segment: PathSegment,
  realAssetsById: Map<string, RealAssetResponse>
) => {
  if (segment.kind === "literal") {
    return segment.value;
  }

  const asset = realAssetsById.get(segment.value);
  if (!asset) {
    return "Deployment ID";
  }

  return `${asset.name} ID`;
};

export const renderVariableSegmentBadgeLabel = (
  segment: PathSegment,
  realAssetsById: Map<string, RealAssetResponse>
) => `{{ ${renderSegmentLabel(segment, realAssetsById)} }}`;

export const getPreviewMatchKey = (item: PreviewDirectoryListItem) =>
  `${item.cruise_real_asset_deployment?.public_id || ""}:${item.path || ""}`;

export const aggregatePreviewMatches = (items: PreviewDirectoryListItem[]) => {
  const groupedItems = new Map<string, PreviewDirectoryListItem>();

  items.forEach((item) => {
    const groupKey = [
      item.cruise_real_asset_deployment?.public_id || "",
      item.path || "",
    ].join(":");

    const existing = groupedItems.get(groupKey);
    if (!existing) {
      groupedItems.set(groupKey, { ...item });
      return;
    }

    existing.direct_file_count =
      (existing.direct_file_count ?? 0) + (item.direct_file_count ?? 0);
    existing.direct_file_size =
      (existing.direct_file_size ?? 0) + (item.direct_file_size ?? 0);
    existing.subdirectory_count = Math.max(
      existing.subdirectory_count ?? 0,
      item.subdirectory_count ?? 0
    );

    if (
      item.latest_asset_modified_dt &&
      (!existing.latest_asset_modified_dt ||
        item.latest_asset_modified_dt > existing.latest_asset_modified_dt)
    ) {
      existing.latest_asset_modified_dt = item.latest_asset_modified_dt;
    }
  });

  return [...groupedItems.values()];
};

export const aggregateObservedSubdirectories = (
  items: DataAssetTreeDirectoryResponse[]
) => {
  const groupedItems = new Map<string, DataAssetTreeDirectoryResponse>();

  items.forEach((item) => {
    const itemPath = item.path || "";
    const existing = groupedItems.get(itemPath);
    if (!existing) {
      groupedItems.set(itemPath, { ...item });
      return;
    }

    existing.child_count = Math.max(
      existing.child_count ?? 0,
      item.child_count ?? 0
    );
    existing.size = (existing.size ?? 0) + (item.size ?? 0);

    if (
      item.asset_modified_dt &&
      (!existing.asset_modified_dt ||
        item.asset_modified_dt > existing.asset_modified_dt)
    ) {
      existing.asset_modified_dt = item.asset_modified_dt;
    }

    existing.is_curated = existing.is_curated || item.is_curated;
    existing.allow_addendum_upload =
      existing.allow_addendum_upload || item.allow_addendum_upload;
    existing.directory_public_id =
      existing.directory_public_id || item.directory_public_id;
    existing.description = existing.description || item.description;
    existing.canonical_path = existing.canonical_path || item.canonical_path;
  });

  return [...groupedItems.values()];
};

export const compareExampleDirectoryItems = (
  a: ExampleDirectoryItem,
  b: ExampleDirectoryItem
) =>
  Number(a.type === "file") - Number(b.type === "file") ||
  a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: "base",
  });

export const toFileSystemItems = (
  subdirectories: DataAssetTreeDirectoryResponse[],
  files: DataAssetWithManifestAndRealAssetDeploymentResponse[]
): ExampleDirectoryItem[] => {
  const items: ExampleDirectoryItem[] = [];

  for (const subdir of subdirectories) {
    items.push({
      name: extractFileName(subdir.path || ""),
      path: subdir.path || "",
      type: "folder",
    });
  }

  for (const file of files) {
    items.push({
      name: extractFileName(file.path || ""),
      path: file.path || "",
      type: "file",
    });
  }

  return items.sort(compareExampleDirectoryItems);
};
